import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminSidebarNavigation from '../../components/ui/AdminSidebarNavigation';
import MetricCard from './components/MetricCard';
import RegistrationChart from './components/RegistrationChart';
import FilterPanel from './components/FilterPanel';
import RegistrationTable from './components/RegistrationTable';
import QuickActionsPanel from './components/QuickActionsPanel';
import RecentActivityFeed from './components/RecentActivityFeed';
import Button from '../../components/ui/Button';
import { adminService } from '../../services/adminService';
import { registrationService } from '../../services/registrationService';
import { exportService } from '../../services/exportService';
import { useAuth } from '../../contexts/AuthContext';


const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [userRole, setUserRole] = useState('super_admin');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  
  // Data states
  const [metricsData, setMetricsData] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [registrationsData, setRegistrationsData] = useState([]);
  const [activitiesData, setActivitiesData] = useState([]);
  const [quickStatsData, setQuickStatsData] = useState({
    pendingReviews: 0,
    recentSubmissions: 0,
    messagesSent: 0,
    activeCities: 0
  });

  // Pagination state
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    total: 0,
    totalPages: 1
  });

  const [filters, setFilters] = useState({
    city: 'all',
    ageGroup: 'all',
    gender: 'all',
    status: 'all',
    schoolNames: [], // Changed to array for multi-select
    dateFrom: ''
  });

  useEffect(() => {
    const storedRole = localStorage.getItem('userRole');
    if (storedRole) {
      setUserRole(storedRole);
      // Redirect comms_user to Communications page
      if (storedRole === 'comms_user') {
        navigate('/admin-communication-center');
        return;
      }
    }
    loadDashboardData();
  }, [navigate]);

  useEffect(() => {
    // Reset to page 1 when filters change
    setPagination(prev => ({ ...prev, page: 1 }));
  }, [filters]);

  useEffect(() => {
    loadRegistrations();
  }, [filters, pagination.page]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError('');

      // Load metrics
      const metrics = await adminService?.getDashboardMetrics();
      
      setMetricsData([
        {
          title: 'Total Registrations',
          value: metrics?.totalRegistrations?.toString(),
          change: null,
          changeType: null,
          icon: 'Users',
          iconColor: 'var(--color-primary)'
        },
        {
          title: 'Pending Reviews',
          value: metrics?.pendingReviews?.toString(),
          change: null,
          changeType: null,
          icon: 'Clock',
          iconColor: 'var(--color-warning)'
        },
        {
          title: 'Approved Teams',
          value: metrics?.approvedTeams?.toString(),
          change: null,
          changeType: null,
          icon: 'CheckCircle',
          iconColor: 'var(--color-success)'
        },
        {
          title: 'Active Cities',
          value: metrics?.activeCities?.toString(),
          change: null,
          changeType: null,
          icon: 'MapPin',
          iconColor: 'var(--color-accent)'
        }
      ]);

      setQuickStatsData({
        pendingReviews: metrics?.pendingReviews,
        recentSubmissions: 0,
        messagesSent: 0,
        activeCities: metrics?.activeCities
      });

      // Load chart data
      const cityData = await adminService?.getRegistrationsByCity();
      setChartData(cityData);

      // Load recent activities
      const activities = await adminService?.getRecentActivities(6);
      setActivitiesData(activities);

      // Load initial registrations
      await loadRegistrations();

    } catch (err) {
      console.error('Error loading dashboard data:', err);
      setError(err?.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const loadRegistrations = async () => {
    try {
      const result = await adminService?.getFilteredRegistrations(filters, {
        page: pagination.page,
        pageSize: pagination.pageSize
      });
      setRegistrationsData(result?.data || []);
      setPagination(prev => ({
        ...prev,
        total: result?.total || 0,
        totalPages: result?.totalPages || 1
      }));
    } catch (err) {
      console.error('Error loading registrations:', err);
      setError(err?.message || 'Failed to load registrations');
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleResetFilters = () => {
    setFilters({
      city: 'all',
      ageGroup: 'all',
      gender: 'all',
      status: 'all',
      schoolNames: [], // Changed to array for multi-select
      dateFrom: ''
    });
  };

  const handleExport = async (exportType = 'registration') => {
    try {
      const format = 'Excel'; // Default to Excel, can be made configurable
      
      // Get actual record count before exporting
      const recordCount = await exportService.getRecordCount('registration', filters);
      
      const exportResult = await exportService.exportRegistrations(format, filters);
      
      // Save export history with actual record count and file information
      const historyResult = await exportService.saveExportHistory(
        exportType, 
        format, 
        filters, 
        recordCount,
        exportResult?.filePath || null,
        exportResult?.fileName || null,
        exportResult?.fileSize || null
      );
      if (!historyResult.success) {
        console.warn('Failed to save export history:', historyResult.error);
        // Optionally show a warning to user
      }
    } catch (error) {
      console.error('Error exporting data:', error);
      alert(`Failed to export data: ${error.message}`);
    }
  };

  const handleViewDetails = (registrationId) => {
    // Get the actual database ID
    const registration = registrationsData.find(r => (r.id === registrationId || r.dbId === registrationId || String(r.id) === String(registrationId) || String(r.dbId) === String(registrationId)));
    const dbId = registration?.dbId || registration?.id || registrationId;
    navigate('/registration-detail-view', { state: { registrationId: dbId, userMode: false } });
  };

  const handleBulkAction = async (action, selectedIds) => {
    if (!selectedIds || selectedIds.length === 0) {
      alert('Please select at least one registration');
      return;
    }

    if (!user?.id) {
      alert('User authentication required');
      return;
    }

    const actionText = action === 'approve' ? 'approve' : action === 'reject' ? 'reject' : 'process';
    const confirmMessage = `Are you sure you want to ${actionText} ${selectedIds.length} registration(s)?`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setBulkActionLoading(true);
    try {
      const results = [];
      const errors = [];

      for (const id of selectedIds) {
        try {
          // Get the actual database ID from the registration
          const registration = registrationsData.find(r => (r.id === id || r.dbId === id || String(r.id) === String(id) || String(r.dbId) === String(id)));
          const dbId = registration?.dbId || registration?.id || id;

          if (action === 'approve') {
            await registrationService.approveRegistration(dbId, user.id);
            results.push({ id: dbId, success: true });
          } else if (action === 'reject') {
            // For bulk reject, use a generic reason
            await registrationService.rejectRegistration(dbId, user.id, 'Bulk rejection - please contact admin for details');
            results.push({ id: dbId, success: true });
          }
        } catch (err) {
          console.error(`Error ${action}ing registration ${id}:`, err);
          errors.push({ id, error: err.message });
        }
      }

      if (errors.length > 0) {
        alert(`${results.length} registration(s) ${actionText}d successfully. ${errors.length} failed.`);
      } else {
        alert(`Successfully ${actionText}d ${results.length} registration(s)`);
      }

      // Reload registrations and dashboard data
      await loadRegistrations();
      await loadDashboardData();
    } catch (err) {
      console.error(`Error performing bulk ${action}:`, err);
      alert(`Failed to ${action} registrations: ${err.message}`);
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userRole');
    navigate('/mobile-authentication');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="bg-destructive/10 text-destructive p-4 rounded-lg mb-4">
            <p className="font-semibold mb-2">Error Loading Dashboard</p>
            <p className="text-sm">{error}</p>
          </div>
          <Button onClick={loadDashboardData}>Retry</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <AdminSidebarNavigation 
        isCollapsed={isSidebarCollapsed}
        onToggle={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
      />
      <div className={`main-content with-sidebar ${isSidebarCollapsed ? 'collapsed' : ''}`}>
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground mb-2">Admin Dashboard</h1>
            <p className="text-sm sm:text-base text-muted-foreground">
              Welcome back! Here's what's happening with MI Junior Cricket Registration.
            </p>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
            {/* Hide notification bell */}
            {false && (
              <Button variant="outline" iconName="Bell" size="icon" className="touch-target">
                <span className="sr-only">Notifications</span>
              </Button>
            )}
            <Button variant="outline" onClick={handleLogout} iconName="LogOut" size="sm" className="touch-target">
              <span className="hidden sm:inline">Logout</span>
              <span className="sm:hidden">Logout</span>
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4 sm:mb-6">
          {metricsData?.map((metric, index) => (
            <MetricCard key={index} {...metric} />
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 mb-4 sm:mb-6">
          <div className="lg:col-span-2">
            <RegistrationChart data={chartData} />
          </div>
          <div>
            <QuickActionsPanel 
              userRole={userRole} 
              stats={quickStatsData}
              onCityUpdate={loadDashboardData}
            />
          </div>
        </div>

        <div className="mb-4 sm:mb-6">
          <FilterPanel
            filters={filters}
            onFilterChange={handleFilterChange}
            onReset={handleResetFilters}
            onExport={handleExport}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
          <div className="lg:col-span-2">
            <RegistrationTable
              registrations={registrationsData}
              onViewDetails={handleViewDetails}
              onBulkAction={handleBulkAction}
              userRole={userRole}
              pagination={pagination}
              onPageChange={(page) => setPagination(prev => ({ ...prev, page }))}
            />
          </div>
          <div>
            <RecentActivityFeed activities={activitiesData} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;